// pages/ToshbaClassList.js
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';

export default function ToshbaClassList() {
  const [classes, setClasses] = useState([]);
  const [discipline, setDiscipline] = useState(null);
  const [howToTeachImage, setHowToTeachImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const disciplinesRes = await fetchWithAuth('/api/disciplines/');
        if (!disciplinesRes.ok) throw new Error('Failed to load disciplines');
        const disciplines = await disciplinesRes.json();
        const toshbaDiscipline = disciplines.find(d => d.name.includes('תושב"ע'));

        if (!toshbaDiscipline) {
          if (!ignore) setError('דיסציפלינת תושב"ע לא נמצאה');
          return;
        }

        if (!ignore) setDiscipline(toshbaDiscipline);

        // Load domains with images
        const domainsRes = await fetchWithAuth(`/api/disciplines/${toshbaDiscipline.id}/toshba-domains/`);
        if (!domainsRes.ok) throw new Error('Failed to load domains');
        const data = await domainsRes.json();
        if (!ignore) setClasses(data); // Using classes state to store domains

        // Load How to Teach image
        const imageRes = await fetchWithAuth(`/api/disciplines/${toshbaDiscipline.id}/toshba-how-to-teach-image/`);
        if (imageRes.ok) {
          const imageData = await imageRes.json();
          if (!ignore) setHowToTeachImage(imageData.image);
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת התחומים');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { label: 'תושב"ע' },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {discipline?.name || 'תושב"ע'}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to="/disciplines"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה לתחומי דעת
        </Link>
      </p>

      {loading && <SkeletonCardGrid count={6} />}

      {error && (
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="grid-autofit">
          {/* Domain cards */}
          {classes.map(domain => (
            <article
              key={domain.id}
              className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1"
            >
              <Link
                to={`/toshba/classes/${domain.route}`}
                className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                style={{ aspectRatio: '3/2' }}
              >
                {domain.image ? (
                  <img
                    src={domain.image}
                    alt={domain.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </Link>
              <div className="p-4 md:p-5">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                  <Link
                    to={`/toshba/classes/${domain.route}`}
                    className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    {domain.name}
                  </Link>
                </h3>
              </div>
            </article>
          ))}

          {/* Special card for How to Teach */}
          <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
            <Link
              to="/toshba/how-to-teach"
              className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
              style={{ aspectRatio: '3/2' }}
            >
              {howToTeachImage ? (
                <img
                  src={howToTeachImage}
                  alt="הכנה למבחן מפמ״ר"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
              />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </Link>
            <div className="p-4 md:p-5">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                <Link
                  to="/toshba/how-to-teach"
                  className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  הכנה למבחן מפמ"ר
                </Link>
              </h3>
            </div>
          </article>
        </div>
      )}

      {!loading && !error && classes.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין תחומים להצגה.
        </p>
      )}
    </div>
  );
}